import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import i18n from 'i18n-js';
import it from './it.json';
import { Config } from '../helpers/config';
import { downloadLocaleTranslations } from '../helpers/locales';
import { useAppDispatch } from '../redux/core';
import { setIsAppInitialized } from '../redux/general/general.actions';
import updateLocale from 'dayjs/plugin/updateLocale';
import dayjs from 'dayjs';

import 'dayjs/locale/it';

dayjs.extend(updateLocale);

const IT = 'it';
export const supportedLanguages = { it };

export type DeepKeys<T, S extends string> = T extends object
  ? S extends `${infer I1}.${infer I2}`
    ? I1 extends keyof T
      ? `${I1}.${DeepKeys<T[I1], I2>}`
      : keyof T & string
    : S extends keyof T
    ? `${S}`
    : keyof T & string
  : '';

export type LanguagesType = keyof typeof supportedLanguages;
export type LocalizationKeys = keyof typeof it;
export type TranslationKeys<S extends string> = DeepKeys<typeof it, S>;
export type CallbackTranslation = <S extends string>(
  scope: DeepKeys<typeof it, S>,
  options?: Object
) => string;
export type CallbackSetTranslation = (language: LanguagesType) => void;

export const localeManager = <S extends string>(
  scope: DeepKeys<typeof it, S>,
  newOptions?: Object
): string => {
  const options = newOptions || { locale: IT };
  return i18n.t(scope, options || {});
};

interface LocalizationContextProps {
  t: CallbackTranslation;
  locale: LanguagesType;
  setLocale: CallbackSetTranslation;
}

i18n.fallbacks = true;
i18n.translations = { it };

export const LocalizationContext = createContext<LocalizationContextProps>({
  t: (scope, options = {}) => i18n.t(scope, { IT, ...options }),
  setLocale: () => {},
  locale: 'it',
});

export const LocalizationProvider: React.FC<any> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [locale, setLocale] = useState<LanguagesType>(IT);

  const t = useMemo((): CallbackTranslation => {
    return (scope, options) => i18n.t(scope, { locale, ...options });
  }, [locale]);

  const setAppLocale = useCallback((language: LanguagesType) => {
    i18n.locale = language;
    dayjs.locale(language);
    setLocale(language);
  }, []);

  useEffect(() => {
    const language = window.navigator.language;
    const languageToSet: keyof typeof supportedLanguages | undefined =
      Object.keys(supportedLanguages).find(
        (k) => language.split('-')[0] === k
      ) as keyof typeof supportedLanguages | undefined;
    dayjs.locale(languageToSet ?? 'it');

    updateLocaleDaysTranslations(
      languageToSet ?? 'it',
      supportedLanguages[languageToSet ?? 'it']
    );

    if (languageToSet && Config.env !== 'test') {
      downloadLocaleTranslations(`${Config.baseCmsUrl}/dict`, languageToSet)
        .then((languageObj) => {
          i18n.translations = {
            ...i18n.translations,
            [languageToSet]: languageObj,
          };
        })
        .finally(() => {
          setAppLocale(languageToSet as LanguagesType);
          dispatch(setIsAppInitialized(true));
        });
    } else {
      dispatch(setIsAppInitialized(true));
    }
  }, [dispatch, setAppLocale]);

  return (
    <LocalizationContext.Provider
      value={{
        t,
        setLocale: setAppLocale,
        locale,
      }}
    >
      {children}
    </LocalizationContext.Provider>
  );
};

function updateLocaleDaysTranslations(
  locale: string,
  localeTranslations?: typeof it
) {
  const translations = localeTranslations ?? it;

  dayjs.updateLocale(locale, {
    weekdays: [
      translations.weekdays.sunday,
      translations.weekdays.monday,
      translations.weekdays.tuesday,
      translations.weekdays.wednesday,
      translations.weekdays.thursday,
      translations.weekdays.friday,
      translations.weekdays.saturday,
    ],
    months: [
      translations.months.january,
      translations.months.february,
      translations.months.march,
      translations.months.april,
      translations.months.may,
      translations.months.june,
      translations.months.july,
      translations.months.august,
      translations.months.september,
      translations.months.october,
      translations.months.november,
      translations.months.december,
    ],
  });
}
